/**
 * admin/actions.ts
 *
 * Mutating Supabase operations for the Stratakey system_admin panel.
 * Every action:
 *   1. Performs the DB operation (update / insert / delete)
 *   2. Writes an audit log entry via logAdminAction()
 *   3. Returns { success, error? }
 *
 * No exceptions are thrown — all errors are caught and returned.
 */

import { supabase } from "@/lib/supabase";
import type { Organization, Unit, StrataConfig } from "@/types/database";

// ─── Response wrapper ─────────────────────────────────────────────────────────

export interface ActionResult {
  success: boolean;
  error?: string;
  data?: any;
}

// ─── Audit log helper ─────────────────────────────────────────────────────────

async function logAdminAction(
  action: string,
  entityType: string,
  entityId: string | null,
  details: Record<string, unknown>,
  actorId: string
): Promise<void> {
  try {
    const { error } = await supabase.from("audit_logs").insert({
      user_id: actorId,
      user_name: "system_admin",
      action,
      entity: entityType,
      detail: JSON.stringify({ entityId, ...details }),
    });
    if (error) {
      console.warn("[admin/logAdminAction] Failed to write audit log:", error.message);
    }
  } catch (err: any) {
    console.warn("[admin/logAdminAction] Exception:", err?.message);
  }
}

// ─── Users ────────────────────────────────────────────────────────────────────

export interface UpdateUserData {
  full_name?: string;
  email?: string;
  role?: string;
  status?: string;
}

export async function updateUser(
  id: string,
  data: UpdateUserData,
  actorId: string
): Promise<ActionResult> {
  try {
    const { error } = await supabase
      .from("profiles")
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      console.error("[admin/updateUser]", error);
      return { success: false, error: error.message };
    }

    await logAdminAction("admin_update_user", "profile", id, { changes: data }, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/updateUser]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function suspendUser(id: string, actorId: string): Promise<ActionResult> {
  try {
    // Mark in profiles with a suspended indicator in a metadata field if available;
    // otherwise log the suspension in audit and update role to mark inactive.
    // We use a dedicated suspended flag via the detail column pattern — actual
    // suspension also disables Supabase auth via the admin API (Edge Function).
    const res = await supabase.functions.invoke("admin-suspend-user", {
      body: { userId: id, suspend: true, actorId },
    });

    if (res.error) {
      const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
      console.error("[admin/suspendUser]", detail);
      return { success: false, error: detail };
    }

    await logAdminAction("admin_suspend_user", "profile", id, { suspended: true }, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/suspendUser]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function unsuspendUser(id: string, actorId: string): Promise<ActionResult> {
  try {
    const res = await supabase.functions.invoke("admin-suspend-user", {
      body: { userId: id, suspend: false, actorId },
    });

    if (res.error) {
      const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
      console.error("[admin/unsuspendUser]", detail);
      return { success: false, error: detail };
    }

    await logAdminAction("admin_unsuspend_user", "profile", id, { suspended: false }, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/unsuspendUser]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function deleteUser(id: string, actorId: string): Promise<ActionResult> {
  try {
    // Soft-delete: remove profile record. Hard Supabase auth deletion requires
    // service-role key — delegate to Edge Function.
    const res = await supabase.functions.invoke("admin-delete-user", {
      body: { userId: id, actorId },
    });

    if (res.error) {
      const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
      console.error("[admin/deleteUser]", detail);
      return { success: false, error: detail };
    }

    await logAdminAction("admin_delete_user", "profile", id, {}, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/deleteUser]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function resetUserPassword(email: string): Promise<ActionResult> {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      console.error("[admin/resetUserPassword]", error);
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (err: any) {
    console.error("[admin/resetUserPassword]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export interface CreateUserData {
  full_name: string;
  email: string;
  role: string;
  plan?: string;
  sendInvite?: boolean;
}

export async function createUser(
  data: CreateUserData,
  actorId: string
): Promise<ActionResult> {
  try {
    const res = await supabase.functions.invoke("admin-create-user", {
      body: { ...data, actorId },
    });

    if (res.error) {
      const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
      console.error("[admin/createUser]", detail);
      return { success: false, error: detail };
    }

    const userId = (res.data as { userId?: string })?.userId ?? null;
    await logAdminAction("admin_create_user", "profile", userId, { email: data.email, role: data.role }, actorId);
    return { success: true, data: res.data };
  } catch (err: any) {
    console.error("[admin/createUser]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function impersonateUser(
  userId: string,
  actorId: string
): Promise<ActionResult & { token?: string }> {
  try {
    const res = await supabase.functions.invoke("admin-impersonate-user", {
      body: { userId, actorId },
    });

    if (res.error) {
      const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
      console.error("[admin/impersonateUser]", detail);
      return { success: false, error: detail };
    }

    await logAdminAction("admin_impersonate_user", "profile", userId, {}, actorId);
    return { success: true, token: (res.data as { token?: string })?.token, data: res.data };
  } catch (err: any) {
    console.error("[admin/impersonateUser]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

// ─── Buildings ────────────────────────────────────────────────────────────────

export async function updateBuilding(
  id: string,
  data: Partial<Organization>,
  actorId: string
): Promise<ActionResult> {
  try {
    const { error } = await supabase
      .from("organizations")
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      console.error("[admin/updateBuilding]", error);
      return { success: false, error: error.message };
    }

    await logAdminAction("admin_update_building", "organization", id, { changes: data }, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/updateBuilding]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function suspendBuilding(id: string, actorId: string): Promise<ActionResult> {
  try {
    // Suspend all hosts in the building
    const { error: hostsErr } = await supabase
      .from("hosts")
      .update({ status: "suspended", updated_at: new Date().toISOString() })
      .eq("organization_id", id);

    if (hostsErr) {
      console.error("[admin/suspendBuilding hosts]", hostsErr);
      return { success: false, error: hostsErr.message };
    }

    // Suspend all units
    const { error: unitsErr } = await supabase
      .from("units")
      .update({ status: "suspended", updated_at: new Date().toISOString() })
      .eq("organization_id", id);

    if (unitsErr) {
      console.error("[admin/suspendBuilding units]", unitsErr);
      return { success: false, error: unitsErr.message };
    }

    await logAdminAction("admin_suspend_building", "organization", id, {}, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/suspendBuilding]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function deleteBuilding(id: string, actorId: string): Promise<ActionResult> {
  try {
    // Record audit log before deletion
    await logAdminAction("admin_delete_building", "organization", id, {}, actorId);

    const { error } = await supabase
      .from("organizations")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("[admin/deleteBuilding]", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err: any) {
    console.error("[admin/deleteBuilding]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export interface CreateBuildingData {
  name: string;
  address: string;
  city?: string;
  adminId: string;
  nightCap: number;
  plan: string;
}

export async function createBuilding(
  data: CreateBuildingData,
  actorId: string
): Promise<ActionResult> {
  try {
    const { data: org, error: orgErr } = await supabase
      .from("organizations")
      .insert({
        name: data.name,
        address: data.address,
        total_units: 0,
        plan: data.plan,
        admin_id: data.adminId,
      })
      .select()
      .single();

    if (orgErr) {
      console.error("[admin/createBuilding]", orgErr);
      return { success: false, error: orgErr.message };
    }

    // Create default strata config with specified night cap
    const { error: configErr } = await supabase.from("strata_config").insert({
      organization_id: org.id,
      max_nights_per_year: data.nightCap,
      min_stay_nights: 1,
      max_guest_count: 8,
      require_insurance: false,
      fee_type: "flat",
      fee_amount: 0,
      pm_can_modify_fees: false,
      collect_municipal_tax: false,
      collect_provincial_tax: false,
      mrdt_rate: 2,
      pst_rate: 8,
      approved_units: [],
      str_policy: "allow_all",
      max_fine_per_day: 1000,
      no_parties_events: false,
      pets_allowed_str: false,
      max_vehicles: 2,
      min_advance_booking_days: 1,
      require_good_neighbour_agreement: false,
      require_council_approval_per_booking: false,
      require_provincial_registration: false,
      require_business_licence: false,
      annual_permit_required: false,
      str_application_fee: 0,
    });

    if (configErr) {
      console.warn("[admin/createBuilding] strata_config insert failed:", configErr.message);
    }

    // Assign admin to building
    const { error: assignErr } = await supabase.from("user_assignments").insert({
      user_id: data.adminId,
      org_id: org.id,
      role: "strata_admin",
      permissions: {},
      assigned_by: actorId,
    });

    if (assignErr) {
      console.warn("[admin/createBuilding] user_assignment insert failed:", assignErr.message);
    }

    await logAdminAction("admin_create_building", "organization", org.id, { name: data.name, plan: data.plan }, actorId);
    return { success: true, data: org };
  } catch (err: any) {
    console.error("[admin/createBuilding]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function assignManager(
  buildingId: string,
  managerId: string,
  actorId: string
): Promise<ActionResult> {
  try {
    const { error } = await supabase
      .from("user_assignments")
      .upsert(
        {
          user_id: managerId,
          org_id: buildingId,
          role: "property_manager",
          permissions: {},
          assigned_by: actorId,
        },
        { onConflict: "user_id,org_id" }
      );

    if (error) {
      console.error("[admin/assignManager]", error);
      return { success: false, error: error.message };
    }

    await logAdminAction("admin_assign_manager", "organization", buildingId, { managerId }, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/assignManager]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

// ─── Units ────────────────────────────────────────────────────────────────────

export async function updateUnit(
  id: string,
  data: Partial<Unit>,
  actorId: string
): Promise<ActionResult> {
  try {
    const { error } = await supabase
      .from("units")
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      console.error("[admin/updateUnit]", error);
      return { success: false, error: error.message };
    }

    await logAdminAction("admin_update_unit", "unit", id, { changes: data }, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/updateUnit]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function deactivateUnit(id: string, actorId: string): Promise<ActionResult> {
  try {
    const { error } = await supabase
      .from("units")
      .update({ status: "inactive", updated_at: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      console.error("[admin/deactivateUnit]", error);
      return { success: false, error: error.message };
    }

    await logAdminAction("admin_deactivate_unit", "unit", id, {}, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/deactivateUnit]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function resetNightCounter(
  id: string,
  reason: string,
  actorId: string
): Promise<ActionResult> {
  try {
    // Reset nights_used on the host record linked to this unit
    const { data: hosts, error: hostsErr } = await supabase
      .from("hosts")
      .select("id")
      .eq("unit_id", id);

    if (hostsErr) {
      console.error("[admin/resetNightCounter hosts lookup]", hostsErr);
      return { success: false, error: hostsErr.message };
    }

    const hostIds = (hosts ?? []).map((h: any) => h.id);
    if (hostIds.length > 0) {
      const { error } = await supabase
        .from("hosts")
        .update({ nights_used: 0, updated_at: new Date().toISOString() })
        .in("id", hostIds);

      if (error) {
        console.error("[admin/resetNightCounter]", error);
        return { success: false, error: error.message };
      }
    }

    await logAdminAction("admin_reset_night_counter", "unit", id, { reason }, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/resetNightCounter]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function reassignHost(
  unitId: string,
  newHostId: string,
  actorId: string
): Promise<ActionResult> {
  try {
    // Update the unit's host_id
    const { error: unitErr } = await supabase
      .from("units")
      .update({ host_id: newHostId, updated_at: new Date().toISOString() })
      .eq("id", unitId);

    if (unitErr) {
      console.error("[admin/reassignHost unit]", unitErr);
      return { success: false, error: unitErr.message };
    }

    // Update the host record to point to this unit
    const { error: hostErr } = await supabase
      .from("hosts")
      .update({ unit_id: unitId, updated_at: new Date().toISOString() })
      .eq("id", newHostId);

    if (hostErr) {
      console.error("[admin/reassignHost host]", hostErr);
      return { success: false, error: hostErr.message };
    }

    await logAdminAction("admin_reassign_host", "unit", unitId, { newHostId }, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/reassignHost]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

// ─── Bookings ─────────────────────────────────────────────────────────────────

export async function approveBooking(id: string, actorId: string): Promise<ActionResult> {
  try {
    const { data: booking, error: fetchErr } = await supabase
      .from("bookings")
      .select("host_id, nights")
      .eq("id", id)
      .single();

    if (fetchErr) {
      console.error("[admin/approveBooking fetch]", fetchErr);
      return { success: false, error: fetchErr.message };
    }

    const { error } = await supabase
      .from("bookings")
      .update({ status: "approved", updated_at: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      console.error("[admin/approveBooking]", error);
      return { success: false, error: error.message };
    }

    // Increment nights_used on the host
    if (booking?.host_id && booking?.nights) {
      const { data: host } = await supabase
        .from("hosts")
        .select("nights_used")
        .eq("id", booking.host_id)
        .single();

      if (host) {
        await supabase
          .from("hosts")
          .update({
            nights_used: (host.nights_used ?? 0) + booking.nights,
            updated_at: new Date().toISOString(),
          })
          .eq("id", booking.host_id);
      }
    }

    await logAdminAction("admin_approve_booking", "booking", id, {}, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/approveBooking]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function denyBooking(
  id: string,
  reason: string,
  actorId: string
): Promise<ActionResult> {
  try {
    const { error } = await supabase
      .from("bookings")
      .update({
        status: "blocked",
        blocked_reason: reason,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id);

    if (error) {
      console.error("[admin/denyBooking]", error);
      return { success: false, error: error.message };
    }

    await logAdminAction("admin_deny_booking", "booking", id, { reason }, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/denyBooking]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function cancelBooking(id: string, actorId: string): Promise<ActionResult> {
  try {
    const { error } = await supabase
      .from("bookings")
      .update({ status: "cancelled", updated_at: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      console.error("[admin/cancelBooking]", error);
      return { success: false, error: error.message };
    }

    await logAdminAction("admin_cancel_booking", "booking", id, {}, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/cancelBooking]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function overrideNightCap(
  bookingId: string,
  reason: string,
  actorId: string
): Promise<ActionResult> {
  try {
    // Approve the booking regardless of night cap
    const { data: booking, error: fetchErr } = await supabase
      .from("bookings")
      .select("host_id, nights")
      .eq("id", bookingId)
      .single();

    if (fetchErr) {
      console.error("[admin/overrideNightCap fetch]", fetchErr);
      return { success: false, error: fetchErr.message };
    }

    const { error } = await supabase
      .from("bookings")
      .update({ status: "approved", updated_at: new Date().toISOString() })
      .eq("id", bookingId);

    if (error) {
      console.error("[admin/overrideNightCap approve]", error);
      return { success: false, error: error.message };
    }

    // Still increment nights_used even over cap for accurate tracking
    if (booking?.host_id && booking?.nights) {
      const { data: host } = await supabase
        .from("hosts")
        .select("nights_used")
        .eq("id", booking.host_id)
        .single();

      if (host) {
        await supabase
          .from("hosts")
          .update({
            nights_used: (host.nights_used ?? 0) + booking.nights,
            updated_at: new Date().toISOString(),
          })
          .eq("id", booking.host_id);
      }
    }

    await logAdminAction("admin_override_night_cap", "booking", bookingId, { reason }, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/overrideNightCap]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

// ─── Compliance ───────────────────────────────────────────────────────────────

export async function updateComplianceRule(
  orgId: string,
  data: Partial<StrataConfig>,
  actorId: string
): Promise<ActionResult> {
  try {
    const { error } = await supabase
      .from("strata_config")
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq("organization_id", orgId);

    if (error) {
      console.error("[admin/updateComplianceRule]", error);
      return { success: false, error: error.message };
    }

    await logAdminAction("admin_update_compliance_rule", "strata_config", orgId, { changes: data }, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/updateComplianceRule]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function updateGlobalDefaultNightCap(
  cap: number,
  actorId: string
): Promise<ActionResult> {
  try {
    const { error } = await supabase
      .from("strata_config")
      .update({ max_nights_per_year: cap, updated_at: new Date().toISOString() })
      .neq("organization_id", "");

    if (error) {
      console.error("[admin/updateGlobalDefaultNightCap]", error);
      return { success: false, error: error.message };
    }

    await logAdminAction("admin_update_global_night_cap", "platform", null, { cap }, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/updateGlobalDefaultNightCap]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function resolveViolation(
  hostId: string,
  actorId: string
): Promise<ActionResult> {
  try {
    const { error } = await supabase
      .from("hosts")
      .update({ status: "approved", updated_at: new Date().toISOString() })
      .eq("id", hostId);

    if (error) {
      console.error("[admin/resolveViolation]", error);
      return { success: false, error: error.message };
    }

    await logAdminAction("admin_resolve_violation", "host", hostId, {}, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/resolveViolation]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function notifyViolatingHost(
  hostId: string,
  actorId: string
): Promise<ActionResult> {
  try {
    // Fetch host details to get user_id and org context
    const { data: host, error: hostErr } = await supabase
      .from("hosts")
      .select(`
        id, user_id, organization_id, nights_used, night_limit,
        unit:units ( unit_number )
      `)
      .eq("id", hostId)
      .single();

    if (hostErr) {
      console.error("[admin/notifyViolatingHost fetch]", hostErr);
      return { success: false, error: hostErr.message };
    }

    const unitNumber = (host as any)?.unit?.unit_number ?? "Unknown";
    const pctUsed =
      host.night_limit > 0
        ? Math.round((host.nights_used / host.night_limit) * 100)
        : 0;

    // Insert in-app notification
    const { error: notifErr } = await supabase.from("notifications").insert({
      user_id: host.user_id,
      organization_id: host.organization_id,
      type: "warning",
      title: "STR Night Limit Exceeded",
      message: `Your STR nights for Unit ${unitNumber} have exceeded the allowed limit (${host.nights_used}/${host.night_limit} nights used, ${pctUsed}%). Please contact your property manager.`,
      read: false,
      data: { link: "/host-dashboard", unit_number: unitNumber },
    });

    if (notifErr) {
      console.error("[admin/notifyViolatingHost notification]", notifErr);
      return { success: false, error: notifErr.message };
    }

    await logAdminAction("admin_notify_violating_host", "host", hostId, { pctUsed }, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/notifyViolatingHost]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

// ─── Subscriptions ────────────────────────────────────────────────────────────

export async function extendTrial(
  userId: string,
  days: number,
  actorId: string
): Promise<ActionResult> {
  try {
    // Fetch current trial_end
    const { data: sub, error: fetchErr } = await supabase
      .from("subscriptions")
      .select("trial_end")
      .eq("user_id", userId)
      .not("stripe_subscription_id", "is", null)
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (fetchErr) {
      console.error("[admin/extendTrial fetch]", fetchErr);
      return { success: false, error: fetchErr.message };
    }

    const currentTrialEnd = sub?.trial_end ? new Date(sub.trial_end) : new Date();
    currentTrialEnd.setDate(currentTrialEnd.getDate() + days);
    const newTrialEnd = currentTrialEnd.toISOString();

    const { error } = await supabase
      .from("subscriptions")
      .update({ trial_end: newTrialEnd, updated_at: new Date().toISOString() })
      .eq("user_id", userId)
      .not("stripe_subscription_id", "is", null);

    if (error) {
      console.error("[admin/extendTrial]", error);
      return { success: false, error: error.message };
    }

    await logAdminAction("admin_extend_trial", "subscription", userId, { days, newTrialEnd }, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/extendTrial]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function cancelSubscription(
  userId: string,
  reason: string,
  actorId: string
): Promise<ActionResult> {
  try {
    const res = await supabase.functions.invoke("cancel-subscription-immediate", {
      body: { userId, reason, actorId },
    });

    if (res.error) {
      const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
      console.error("[admin/cancelSubscription]", detail);
      return { success: false, error: detail };
    }

    await logAdminAction("admin_cancel_subscription", "subscription", userId, { reason }, actorId);
    return { success: true, data: res.data };
  } catch (err: any) {
    console.error("[admin/cancelSubscription]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function upgradeSubscription(
  userId: string,
  newPlan: string,
  actorId: string
): Promise<ActionResult> {
  try {
    const res = await supabase.functions.invoke("admin-upgrade-subscription", {
      body: { userId, newPlan, actorId },
    });

    if (res.error) {
      const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
      console.error("[admin/upgradeSubscription]", detail);
      return { success: false, error: detail };
    }

    await logAdminAction("admin_upgrade_subscription", "subscription", userId, { newPlan }, actorId);
    return { success: true, data: res.data };
  } catch (err: any) {
    console.error("[admin/upgradeSubscription]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function markAsPaid(
  subscriptionId: string,
  actorId: string
): Promise<ActionResult> {
  try {
    const { error } = await supabase
      .from("subscriptions")
      .update({ status: "active", updated_at: new Date().toISOString() })
      .eq("id", subscriptionId);

    if (error) {
      console.error("[admin/markAsPaid]", error);
      return { success: false, error: error.message };
    }

    await logAdminAction("admin_mark_as_paid", "subscription", subscriptionId, {}, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/markAsPaid]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function refundPayment(
  customerId: string,
  reason: string,
  actorId: string
): Promise<ActionResult> {
  try {
    const res = await supabase.functions.invoke("admin-refund-payment", {
      body: { customerId, reason, actorId },
    });

    if (res.error) {
      const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
      console.error("[admin/refundPayment]", detail);
      return { success: false, error: detail };
    }

    await logAdminAction("admin_refund_payment", "stripe_customer", customerId, { reason }, actorId);
    return { success: true, data: res.data };
  } catch (err: any) {
    console.error("[admin/refundPayment]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

// ─── Messages ─────────────────────────────────────────────────────────────────

export async function flagMessage(id: string, actorId: string): Promise<ActionResult> {
  try {
    // Store a flag note in the audit log — messages table may not have a flag column
    await logAdminAction("admin_flag_message", "message", id, {}, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/flagMessage]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function deleteMessage(id: string, actorId: string): Promise<ActionResult> {
  try {
    await logAdminAction("admin_delete_message", "message", id, {}, actorId);

    const { error } = await supabase.from("messages").delete().eq("id", id);

    if (error) {
      console.error("[admin/deleteMessage]", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err: any) {
    console.error("[admin/deleteMessage]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

// ─── Announcements ────────────────────────────────────────────────────────────

export interface CreateAnnouncementData {
  title: string;
  body: string;
  audience: string;
  delivery: string;
  scheduledAt?: string;
}

export async function createAnnouncement(
  data: CreateAnnouncementData,
  actorId: string
): Promise<ActionResult> {
  try {
    const { data: announcement, error } = await supabase
      .from("announcements")
      .insert({
        title: data.title,
        body: data.body,
        audience: data.audience,
        delivery: data.delivery,
        scheduled_at: data.scheduledAt ?? null,
        author_id: actorId,
      })
      .select()
      .single();

    if (error) {
      console.error("[admin/createAnnouncement]", error);
      return { success: false, error: error.message };
    }

    await logAdminAction("admin_create_announcement", "announcement", announcement.id, { title: data.title, audience: data.audience }, actorId);
    return { success: true, data: announcement };
  } catch (err: any) {
    console.error("[admin/createAnnouncement]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function deleteAnnouncement(id: string, actorId: string): Promise<ActionResult> {
  try {
    await logAdminAction("admin_delete_announcement", "announcement", id, {}, actorId);

    const { error } = await supabase.from("announcements").delete().eq("id", id);

    if (error) {
      console.error("[admin/deleteAnnouncement]", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err: any) {
    console.error("[admin/deleteAnnouncement]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

// ─── Settings ─────────────────────────────────────────────────────────────────

export async function updatePlatformSettings(
  data: Record<string, unknown>,
  actorId: string
): Promise<ActionResult> {
  try {
    const { error } = await supabase
      .from("platform_settings")
      .upsert({ ...data, updated_at: new Date().toISOString() });

    if (error) {
      console.error("[admin/updatePlatformSettings]", error);
      return { success: false, error: error.message };
    }

    await logAdminAction("admin_update_platform_settings", "platform", null, { keys: Object.keys(data) }, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/updatePlatformSettings]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function updateFeatureFlag(
  flag: string,
  value: boolean,
  actorId: string
): Promise<ActionResult> {
  try {
    const { error } = await supabase
      .from("feature_flags")
      .upsert({ flag_name: flag, enabled: value, updated_at: new Date().toISOString() }, { onConflict: "flag_name" });

    if (error) {
      console.error("[admin/updateFeatureFlag]", error);
      return { success: false, error: error.message };
    }

    await logAdminAction("admin_update_feature_flag", "platform", null, { flag, value }, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/updateFeatureFlag]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function updatePricing(
  data: Record<string, unknown>,
  actorId: string
): Promise<ActionResult> {
  try {
    const res = await supabase.functions.invoke("admin-update-pricing", {
      body: { ...data, actorId },
    });

    if (res.error) {
      const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
      console.error("[admin/updatePricing]", detail);
      return { success: false, error: detail };
    }

    await logAdminAction("admin_update_pricing", "platform", null, { keys: Object.keys(data) }, actorId);
    return { success: true, data: res.data };
  } catch (err: any) {
    console.error("[admin/updatePricing]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function inviteAdmin(
  email: string,
  role: string,
  actorId: string
): Promise<ActionResult> {
  try {
    const token = crypto.randomUUID();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);

    const { data: invitation, error } = await supabase
      .from("invitations")
      .insert({
        email,
        token,
        org_id: "system",
        role: role as any,
        invited_by: actorId,
        accepted: false,
        expires_at: expiresAt.toISOString(),
      })
      .select()
      .single();

    if (error) {
      console.error("[admin/inviteAdmin]", error);
      return { success: false, error: error.message };
    }

    await logAdminAction("admin_invite_admin", "invitation", invitation.id, { email, role }, actorId);
    return { success: true, data: invitation };
  } catch (err: any) {
    console.error("[admin/inviteAdmin]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function revokeAdmin(userId: string, actorId: string): Promise<ActionResult> {
  try {
    // Downgrade the role from system_admin to a non-privileged role
    const { error } = await supabase
      .from("profiles")
      .update({ role: "individual", updated_at: new Date().toISOString() })
      .eq("id", userId)
      .eq("role", "system_admin");

    if (error) {
      console.error("[admin/revokeAdmin]", error);
      return { success: false, error: error.message };
    }

    await logAdminAction("admin_revoke_admin", "profile", userId, {}, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/revokeAdmin]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function flushAllSessions(actorId: string): Promise<ActionResult> {
  try {
    const res = await supabase.functions.invoke("admin-flush-sessions", {
      body: { actorId },
    });

    if (res.error) {
      const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
      console.error("[admin/flushAllSessions]", detail);
      return { success: false, error: detail };
    }

    await logAdminAction("admin_flush_all_sessions", "platform", null, {}, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/flushAllSessions]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}

export async function enableMaintenanceMode(actorId: string): Promise<ActionResult> {
  try {
    const { error } = await supabase
      .from("platform_settings")
      .upsert(
        { key: "maintenance_mode", value: true, updated_at: new Date().toISOString() },
        { onConflict: "key" }
      );

    if (error) {
      console.error("[admin/enableMaintenanceMode]", error);
      return { success: false, error: error.message };
    }

    await logAdminAction("admin_enable_maintenance_mode", "platform", null, {}, actorId);
    return { success: true };
  } catch (err: any) {
    console.error("[admin/enableMaintenanceMode]", err);
    return { success: false, error: err.message ?? "Unknown error" };
  }
}
